import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import type { ParsedUrlQueryInput } from 'querystring';
import { useCallback, useEffect } from 'react';
import * as atoms from '../atoms';
import { CheckoutBaBType, ProductTypeOption, SubscriptionType } from '../constants';
import { useBabSanity } from './context';

export const useBundleStore = () => {
  const router = useRouter();
  const setSecondStepOption = useAtom(atoms.secondStepOptionAtom)[1];
  const [secondStepVariant, setSecondStepVariant] = useAtom(atoms.secondStepVariantAtom);
  const setFirstStepOption = useAtom(atoms.firstStepOptionAtom)[1];
  const [, setStep] = useAtom(atoms.stepAtom);
  const [, setSelectedType] = useAtom(atoms.selectedTypeAtom);
  const [, setLanderUrl] = useAtom(atoms.landerUrl);
  const { firstVariant, secondVariant } = useBabSanity();

  const replaceRoute = useCallback(
    (query: ParsedUrlQueryInput) =>
      router.replace({ pathname: router.pathname, query }, undefined, { shallow: true }),
    [router]
  );

  useEffect(() => {
    if (!router.isReady) return;

    if (router.query.lander) {
      setLanderUrl(router.query.lander as string);
    }

    // Handle step from URL
    const stepFromUrl = router.query.step ? parseInt(router.query.step as string, 10) : 1;
    setStep(stepFromUrl);

    // Handle product selection and variant
    const isTravelPacks = router.query.product === 'travel-packs';
    const variantToUse = isTravelPacks ? secondVariant : firstVariant;

    // Set the first step option based on the URL parameter
    setFirstStepOption(
      isTravelPacks ? ProductTypeOption.SECOND_PRODUCT : ProductTypeOption.FIRST_PRODUCT
    );

    // Always set secondStepVariant when product changes
    setSecondStepVariant(variantToUse);

    // Handle second step options
    const defaultOptionFromSanity = secondStepVariant?.defaultVariantChoice || 'doubleSubscription';
    const defaultSelectedSubscription = variantToUse[defaultOptionFromSanity];

    // Map the defaultOptionFromSanity to a SubscriptionType
    const getDefaultSubscriptionType = (option: string) => {
      switch (option) {
        case 'singleSubscription':
          return SubscriptionType.SINGLE;
        case 'doubleSubscription':
          return SubscriptionType.DOUBLE;
        case 'familySubscription':
          return SubscriptionType.FAMILY_PLAN;
        case 'oneTime':
          return SubscriptionType.ONE_TIME;
        default:
          return SubscriptionType.SINGLE;
      }
    };

    const defaultType = getDefaultSubscriptionType(defaultOptionFromSanity);

    // Set the selected type from URL or default from Sanity
    const typeFromUrl = Array.isArray(router.query.type)
      ? (router.query.type[0] as SubscriptionType)
      : (router.query.type as SubscriptionType);

    // Validate if the typeFromUrl is a valid SubscriptionType
    const isValidType = Object.values(SubscriptionType).includes(typeFromUrl as SubscriptionType);
    const finalType = isValidType ? typeFromUrl : defaultType;

    // Remove type from URL if invalid
    if (!isValidType && router.query.type) {
      const { type: _type, ...restQuery } = router.query;
      replaceRoute(restQuery);
    }

    setSelectedType(finalType);

    // Get the correct product based on the selected type
    let selectedProduct = defaultSelectedSubscription; // Set default first
    switch (finalType) {
      case SubscriptionType.SINGLE:
        selectedProduct = variantToUse.singleSubscription || defaultSelectedSubscription;
        break;
      case SubscriptionType.DOUBLE:
        selectedProduct = variantToUse.doubleSubscription || defaultSelectedSubscription;
        break;
      case SubscriptionType.FAMILY_PLAN:
        selectedProduct = variantToUse.familySubscription || defaultSelectedSubscription;
        break;
      case SubscriptionType.ONE_TIME:
        selectedProduct = variantToUse.oneTime || defaultSelectedSubscription;
        break;
      default:
        selectedProduct = defaultSelectedSubscription;
        break;
    }

    if (selectedProduct) {
      setSecondStepOption({
        product: selectedProduct,
        type:
          finalType === SubscriptionType.ONE_TIME
            ? CheckoutBaBType.ONE_TIME
            : CheckoutBaBType.SUBSCRIPTION,
        quantity: selectedProduct.quantity
      });
    }
  }, [
    router.isReady,
    router.query,
    router.pathname,
    firstVariant,
    secondVariant,
    setStep,
    setSecondStepOption,
    setSecondStepVariant,
    setFirstStepOption,
    setSelectedType,
    secondStepVariant?.defaultVariantChoice,
    replaceRoute,
    setLanderUrl
  ]);

  return {};
};
